"use client";

import { useEffect, useState } from "react";
import type { DocCategory, DocArticle } from "@/lib/siteContent";
import DocsSidebar from "./DocsSidebar";
import DocsContent from "./DocsContent";
import Icon from "@/components/ui/AppIcon";
import type { PageTexts } from "@/lib/siteTexts";

// Wording comes from Admin -> Тексты страниц (documentation.*).
type Texts = PageTexts<"documentation">;

export default function DocsInteractive({
  categories,
  articles,
  texts: t,
}: {
  categories: DocCategory[];
  articles: DocArticle[];
  texts: Texts;
}) {
  const [activeSlug, setActiveSlug] = useState(() =>
    articles.length > 0 && !articles.some((a) => a.slug === "quickstart") ? articles[0].slug : "quickstart"
  );
  const [searchQuery, setSearchQuery] = useState("");

  // /documentation#<slug> opens that article (the panel links to articles this way).
  useEffect(() => {
    const open = () => {
      let slug = "";
      try {
        slug = decodeURIComponent(window.location.hash.slice(1));
      } catch {
        return; // a malformed fragment (#%): keep the current article
      }
      if (slug && articles.some((a) => a.slug === slug)) {
        setActiveSlug(slug);
        setSearchQuery('');
      }
    };
    open();
    window.addEventListener("hashchange", open);
    return () => window.removeEventListener("hashchange", open);
  }, [articles]);

  const handleSelect = (slug: string) => {
    setActiveSlug(slug);
    setSearchQuery('');
    window.history.replaceState(null, "", `#${encodeURIComponent(slug)}`);
  };

  const activeArticle = articles.find((a) => a.slug === activeSlug) || null;

  const searchResults = searchQuery.trim()
    ? articles.filter(
        (a) =>
          a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (a.excerpt || '').toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  const quickLinks = [
    { slug: 'quickstart', label: t["quick.quickstart"], icon: 'RocketLaunchIcon' },
    { slug: 'api-auth', label: t["quick.api"], icon: 'CodeBracketIcon' },
    { slug: 'vm-create', label: t["quick.vm"], icon: 'ServerIcon' },
    { slug: 'object-storage', label: t["quick.storage"], icon: 'CubeIcon' },
  ].filter((link) => articles.some((a) => a.slug === link.slug));

  return (
    <div className="max-w-7xl mx-auto px-6 pb-20 relative z-10">
      <div className="mb-8">
        <div className="relative max-w-2xl">
          <Icon name="MagnifyingGlassIcon" size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#4A6080]" />
          <input
            type="text"
            placeholder={t["search.placeholder"]}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-[rgba(15,26,48,0.8)] border border-[rgba(79,124,255,0.2)] rounded-xl pl-11 pr-4 py-3.5 text-[#E8F0FF] text-sm placeholder-[#4A6080] focus:outline-none focus:border-[rgba(0,212,255,0.4)] transition-all"
          />
          <kbd className="absolute right-4 top-1/2 -translate-y-1/2 px-2 py-0.5 rounded text-xs text-[#4A6080] border border-[rgba(79,124,255,0.15)] font-mono">
            ⌘K
          </kbd>
        </div>
        {searchQuery.trim() && (
          <div className="mt-2 max-w-2xl glass rounded-xl border border-[rgba(79,124,255,0.2)] overflow-hidden">
            {searchResults.length === 0 ? (
              <div className="px-4 py-3 text-[#4A6080] text-sm">{t["search.empty"]}</div>
            ) : (
              searchResults.map((article) => (
                <button
                  key={article.id}
                  onClick={() => handleSelect(article.slug)}
                  className="w-full text-left px-4 py-3 hover:bg-[rgba(79,124,255,0.08)] transition-all border-b border-[rgba(79,124,255,0.08)] last:border-0"
                >
                  <div className="text-[#E8F0FF] text-sm font-medium">{article.title}</div>
                  {article.excerpt && (
                    <div className="text-[#4A6080] text-xs mt-0.5 line-clamp-1">{article.excerpt}</div>
                  )}
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {quickLinks.length > 0 && (
        <div className="flex flex-wrap gap-3 mb-8">
          {quickLinks.map((link) => (
            <button
              key={link.slug}
              onClick={() => handleSelect(link.slug)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                activeSlug === link.slug
                  ? "bg-[rgba(0,212,255,0.1)] text-[#00D4FF] border border-[rgba(0,212,255,0.3)]"
                  : "glass border border-[rgba(79,124,255,0.15)] text-[#8BA3CC] hover:text-white hover:border-[rgba(79,124,255,0.3)]"
              }`}
            >
              <Icon name={link.icon as any} size={14} />
              {link.label}
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-6">
        <DocsSidebar
          categories={categories}
          articles={articles}
          activeSlug={activeSlug}
          onSelect={handleSelect}
        />
        <DocsContent article={activeArticle} texts={t} />
      </div>
    </div>
  );
}